/*
 * What stands first in a list of things logged before — `/specs/features/food-order.spec.md`.
 *
 * Three things decide it: how recently a thing went out, how often it goes out, and whether it
 * is a thing of this part of the day. A yogurt bowl at eight in the morning and a pasta bake at
 * eight in the evening are each the obvious answer at their own hour and neither is at the
 * other's, so the same list read at breakfast and at dinner is not the same list.
 *
 * Every app works this out for itself, off what it holds, with no server to ask — so every
 * number here is the spec's, and the Android port in `data/FoodOrder.kt` runs the same test
 * cases.
 */

/** The five stretches a clock time can fall in, in the order the day runs through them. */
export const PARTS_OF_THE_DAY = ['morning', 'midday', 'afternoon', 'evening', 'night'] as const
export type PartOfTheDay = (typeof PARTS_OF_THE_DAY)[number]

/** Where each part begins, in minutes past midnight. Night runs from the last of them to the first. */
const PART_BEGINS: readonly (readonly [PartOfTheDay, number])[] = [
  ['morning', 5 * 60],
  ['midday', 11 * 60],
  ['afternoon', 15 * 60],
  ['evening', 18 * 60],
  ['night', 22 * 60],
]

/** How many days back the times of a food are counted over. */
export const RECENT_DAYS = 90

/**
 * How many of a meal's logs its parts of the day are read off — the newest of them, and no
 * further back, because that is as many as any app holds of a meal. Both ports name this one
 * number so that one account is not offered two different orders.
 */
export const LOGS_READ = 5

/** How many times it takes for how often to be worth half of its share. */
export const OFTEN_HALF = 10

/** How many times must have fallen in a part of the day before that says anything at all. */
export const AFFINITY_SAID = 3

/** What each of the three is worth of the whole. They come to one. */
const RECENCY_SHARE = 0.5
const OFTEN_SHARE = 0.2
const AFFINITY_SHARE = 0.3

/** How many places worth is rounded to, so two ports agree on the order. */
const PLACES = 6

/**
 * Rounded to [PLACES] places by multiplying up, adding a half and taking the floor — the very
 * steps the Kotlin port takes, rather than merely the same number of places. Six places is not
 * an answer on its own: a value sitting on a binary near-half rounds one way by these steps and
 * the other by a decimal-exact rounding, and one such disagreement is one account seeing two
 * different orders on its two devices.
 */
export function rounded(at: number): number {
  const scale = 10 ** PLACES
  return Math.floor(at * scale + 0.5) / scale
}

const DAY = 86_400_000

/** One thing standing in such a list, said as the three questions asked of it. */
export interface Candidate {
  /** When it was last logged, and null where it never has been. */
  readonly moment: number | null
  /** How many times it has gone out, over whatever window its kind is read over. */
  readonly times: number
  /**
   * How many of its times it could read a part of the day off. All of a food's, but only the
   * handful of logs a meal carries — so affinity is a share of what could be read and not of
   * an all-time count the parts were never read from.
   */
  readonly readTimes: number
  /** How many of its read times fell in the part of the day the list is being read in. */
  readonly inPart: number
  /** What it is called, which is the last word on a tie. */
  readonly name: string
}

/** The part of the day a clock time falls in. */
export function partOfTheDay(hour: number, minute = 0): PartOfTheDay {
  const at = hour * 60 + minute
  let part: PartOfTheDay = 'night'
  for (const [named, begins] of PART_BEGINS) {
    if (at >= begins) {
      part = named
    }
  }
  return part
}

/** The part of the day a moment falls in, by the clock the device is set to. */
export function partOfMoment(at: number): PartOfTheDay {
  const when = new Date(at)
  return partOfTheDay(when.getHours(), when.getMinutes())
}

/**
 * What a candidate is worth now, between nought and one — the spec's own arithmetic, rounded
 * to six places before anybody compares two of them.
 */
export function worthOf(candidate: Candidate, now: number): number {
  const { moment, times, readTimes, inPart } = candidate
  const recency = moment === null ? 0 : 1 / (1 + Math.floor(Math.max(0, now - moment) / DAY))
  const often = times <= 0 ? 0 : times / (times + OFTEN_HALF)
  /*
   * A habit said on one or two occasions is not a habit: a list ordered on it would be
   * inventing one, so the part of the day says nothing until three times have fallen in it.
   */
  const affinity = readTimes <= 0 || inPart < AFFINITY_SAID ? 0 : inPart / readTimes
  const worth = RECENCY_SHARE * recency + OFTEN_SHARE * often + AFFINITY_SHARE * affinity
  return rounded(worth)
}

/**
 * A list placed by what each thing is worth, the greatest first. Ties go to the later moment,
 * then to the name; one never logged stands after one with a moment whatever it is called,
 * being worth nought.
 *
 * What it was handed is left alone: the placed list is a new one.
 */
export function placedByWorth<T>(rows: readonly T[], now: number, of: (row: T) => Candidate): T[] {
  const held = rows.map((row) => {
    const candidate = of(row)
    return { row, candidate, worth: worthOf(candidate, now) }
  })
  held.sort((a, b) => {
    if (a.worth !== b.worth) {
      return b.worth - a.worth
    }
    const at = a.candidate.moment
    const bt = b.candidate.moment
    if (at !== bt) {
      if (at === null) {
        return 1
      }
      if (bt === null) {
        return -1
      }
      return bt - at
    }
    /*
     * Lowercased and compared character by character, which is the one rule both ports can
     * hold to: a locale-aware comparison and a code-unit one disagree on "apple" against
     * "Banana", and the Kotlin port has no locale collator to match.
     */
    const named = a.candidate.name.toLowerCase()
    const beside = b.candidate.name.toLowerCase()
    return named < beside ? -1 : named > beside ? 1 : 0
  })
  return held.map((each) => each.row)
}
